import React, { useState, useEffect, useRef, useMemo } from 'react';
import AsyncSelect from "react-select/async";
import { customFetch } from '../apiClient';
import MedicineDuePaymentReceiptModal from './MedicineDuePaymentReceiptModal.jsx';

// ═══════════════════════════════════════════════════════════════════
// SELECT STYLES (matches MedicineSaleForm)
// ═══════════════════════════════════════════════════════════════════
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '31px',
    height: '31px',
    fontSize: '13px',
    borderRadius: '4px',
    borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
  }),
  valueContainer: (p) => ({ ...p, height: '31px', padding: '0 6px' }),
  input: (p) => ({ ...p, margin: '0px' }),
  indicatorsContainer: (p) => ({ ...p, height: '31px' }),
  dropdownIndicator: (p) => ({ ...p, padding: '2px' }),
  clearIndicator: (p) => ({ ...p, padding: '2px' }),
  menu: (p) => ({ ...p, zIndex: 9999 }),
  option: (p, s) => ({
    ...p,
    fontSize: '13px',
    backgroundColor: s.isFocused ? "#e9ecef" : "white",
    color: "black",
    padding: '4px 10px',
  }),
};

// ═══════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════
const round2 = (v) => (Number.isFinite(v) ? parseFloat(v.toFixed(2)) : 0);

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "-";
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
    .toString().padStart(2, '0')}/${d.getFullYear()}`;
};

const generateIdempotencyKey = () =>
  'dpc-' + Date.now() + '-' + Math.random().toString(36).slice(2, 11);

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const MedicineDuePaymentCollect = () => {
  // ─── Customer State ─────────────────────────────────────────────
  const [patientType, setPatientType] = useState("MEDICUST");
  const [customer, setCustomer] = useState({
    id: null,
    name: "",
    age: "",
    gender: "",
    mobileNo: "",
    address: "",
    uhidNo: "",
    ipdId: null,
  });

  // ─── Bills & Allocations ────────────────────────────────────────
  const [pendingBills, setPendingBills] = useState([]);
  const [allocations, setAllocations] = useState({}); // { billNo: amount }
  const [loadingBills, setLoadingBills] = useState(false);

  // ─── Payment State ──────────────────────────────────────────────
  const [paymentMode, setPaymentMode] = useState("CASH");
  const [cashAmount, setCashAmount] = useState(0);
  const [upiAmount, setUpiAmount] = useState(0);
  const [remarks, setRemarks] = useState("");

  // ─── UI State ───────────────────────────────────────────────────
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  // ─── Company Info (for Receipt Header) ─────────────────────────
  const [companyInfo, setCompanyInfo] = useState(null);

  const idempotencyKeyRef = useRef(generateIdempotencyKey());

  // ═══════════════════════════════════════════════════════════════
  // FETCH COMPANY INFO (one-time, for receipt header)
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const res = await customFetch('/PharmaCompany');
        if (res.ok) {
          const data = await res.json();
          if (data && data.length > 0) setCompanyInfo(data[0]);
        }
      } catch (err) {
        console.error("Failed to fetch company info", err);
      }
    };
    fetchCompanyInfo();
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // COMPUTED TOTALS (live calculation)
  // ═══════════════════════════════════════════════════════════════
  const totals = useMemo(() => {
    const totalDue = pendingBills.reduce((sum, b) => sum + (b.dueAmount || 0), 0);
    const totalAllocated = Object.values(allocations).reduce(
      (sum, a) => sum + (parseFloat(a) || 0), 0
    );
    const totalReceived = (parseFloat(cashAmount) || 0) + (parseFloat(upiAmount) || 0);
    const balanceDue = totalDue - totalAllocated;
    const mismatch = Math.abs(totalAllocated - totalReceived) > 0.01;

    return {
      totalDue: round2(totalDue),
      totalAllocated: round2(totalAllocated),
      totalReceived: round2(totalReceived),
      balanceDue: round2(balanceDue),
      mismatch,
    };
  }, [pendingBills, allocations, cashAmount, upiAmount]);

  // ═══════════════════════════════════════════════════════════════
  // CUSTOMER SEARCH (reuses existing endpoints)
  // ═══════════════════════════════════════════════════════════════
  const loadCustomerOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 1) return [];

    let endpoint = "";
    let queryParam = "term";

    if (patientType === "MEDICUST") {
      endpoint = "/MediCustDT/search";
    } else if (patientType === "OPD") {
      endpoint = "/MediCustDT/searchopd";
    } else if (patientType === "IPD") {
      endpoint = "/Ipds/list";
      queryParam = "search";
    }

    try {
      const res = await customFetch(
        `${endpoint}?${queryParam}=${encodeURIComponent(inputValue.trim())}`
      );
      if (!res.ok) return [];
      const data = await res.json();

      return data.map((c) => {
        const realIpdId = c.ipD_ID || c.IPD_ID || c.ipdId || c.ipd_id || c.id || null;
        return {
          value: patientType === "IPD" ? realIpdId : (c.customerID || c.id),
          label: `${c.customerName || c.name} | UHID: ${c.uhiD_No || c.uhidNo || "NA"} | Age: ${c.age || "NA"} | Mobile: ${c.mobileNo || "NA"}`,
          data: c,
        };
      });
    } catch {
      return [];
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // CUSTOMER SELECT
  // ═══════════════════════════════════════════════════════════════
  const handleCustomerSelect = async (selected) => {
    setPendingBills([]);
    setAllocations({});
    setError("");
    setSuccess("");

    if (!selected) {
      setCustomer({
        id: null, name: "", age: "", gender: "",
        mobileNo: "", address: "", uhidNo: "", ipdId: null,
      });
      return;
    }

    const c = selected.data;
    const customerData = {
      id: selected.value,
      name: c.name || c.customerName || "",
      age: c.age || "",
      gender: c.gender || "",
      mobileNo: c.mobileNo || "",
      address: c.address || "",
      uhidNo: c.uhiD_No || c.uhidNo || "",
      ipdId: patientType === "IPD" ? selected.value : null,
    };
    setCustomer(customerData);

    // For OPD/IPD, the selected.value is OpdId/IpdId.
    // Bills are linked by CID (MediCustDT.CustomerID).
    // OPD/IPD me selected ID = OpdId/IpdId, not CustomerID.
    // So we need to search by mobile/name in MediCustDT to find the linked record.

    if (patientType === "MEDICUST") {
      await fetchCustomerDues(customerData.id);
    } else {
      // For OPD/IPD: try to find the linked MediCustDT by mobile number
      await findAndFetchByMobile(customerData);
    }
  };

  const findAndFetchByMobile = async (custData) => {
    if (!custData.mobileNo) {
      setError("Cannot fetch bills: mobile number is missing.");
      return;
    }

    setLoadingBills(true);
    try {
      // Search MediCustDT by mobile
      const res = await customFetch(
        `/MediCustDT/search?term=${encodeURIComponent(custData.mobileNo)}`
      );
      if (!res.ok) {
        setError("Could not find this patient's pharmacy account.");
        return;
      }
      const candidates = await res.json();

      // Find best match by mobile + name
      const match = candidates.find(c =>
        c.mobileNo === custData.mobileNo &&
        (c.customerName || '').trim().toLowerCase() === (custData.name || '').trim().toLowerCase()
      ) || candidates[0];

      if (!match || !match.customerID) {
        setError(
          `No pharmacy bills found for ${custData.name} (${custData.mobileNo}). ` +
          `Bills are created when medicines are sold to this patient.`
        );
        setLoadingBills(false);
        return;
      }

      // Update customer ID to the actual MediCustDT.CustomerID
      setCustomer(prev => ({ ...prev, id: match.customerID }));
      await fetchCustomerDues(match.customerID);
    } catch (err) {
      setError(err.message || "Error finding pharmacy account");
      setLoadingBills(false);
    }
  };

  const fetchCustomerDues = async (customerId) => {
    setLoadingBills(true);
    setError("");
    try {
      const res = await customFetch(`/DuePayment/customer/${customerId}/dues`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to fetch pending bills");
      }
      const data = await res.json();
      setPendingBills(data.bills || []);

      if ((data.bills || []).length === 0) {
        setError("This customer has no pending bills. 🎉");
      }
    } catch (err) {
      setError(err.message || "Error fetching bills");
      setPendingBills([]);
    } finally {
      setLoadingBills(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // ALLOCATION HANDLERS
  // ═══════════════════════════════════════════════════════════════
  const handleAllocationChange = (billNo, value) => {
    if (value === "" || value === null) {
      const next = { ...allocations };
      delete next[billNo];
      setAllocations(next);
      return;
    }

    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return;

    const bill = pendingBills.find(b => b.billNo === billNo);
    if (!bill) return;

    // Cap at bill's due amount
    const capped = Math.min(num, bill.dueAmount);
    setAllocations(prev => ({ ...prev, [billNo]: capped }));
  };

  const handlePayFullBill = (bill) => {
    setAllocations(prev => ({ ...prev, [bill.billNo]: bill.dueAmount }));
  };

  const handlePayAllBills = () => {
    const all = {};
    pendingBills.forEach(b => { all[b.billNo] = b.dueAmount; });
    setAllocations(all);

    // Auto-set payment amounts
    const total = pendingBills.reduce((s, b) => s + b.dueAmount, 0);
    if (paymentMode === "CASH") {
      setCashAmount(total);
      setUpiAmount(0);
    } else if (paymentMode === "UPI") {
      setUpiAmount(total);
      setCashAmount(0);
    }
  };

  const handleClearAllocations = () => {
    setAllocations({});
    setCashAmount(0);
    setUpiAmount(0);
  };

  // ═══════════════════════════════════════════════════════════════
  // PAYMENT MODE CHANGE — auto-adjust amounts
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (paymentMode === "CASH") {
      setCashAmount(totals.totalAllocated);
      setUpiAmount(0);
    } else if (paymentMode === "UPI") {
      setUpiAmount(totals.totalAllocated);
      setCashAmount(0);
    }
    // For BOTH: user manages manually
  }, [paymentMode, totals.totalAllocated]);

  // ═══════════════════════════════════════════════════════════════
  // SUBMIT
  // ═══════════════════════════════════════════════════════════════
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // ─── Validations ───────────────────────────────────────────────
    if (!customer.id) {
      setError("Please select a customer first.");
      return;
    }

    const allocationEntries = Object.entries(allocations)
      .filter(([, amt]) => parseFloat(amt) > 0);

    if (allocationEntries.length === 0) {
      setError("Please enter amount for at least one bill.");
      return;
    }

    if (totals.totalAllocated <= 0) {
      setError("Total allocation must be greater than zero.");
      return;
    }

    if (totals.mismatch) {
      setError(
        `Payment mismatch: Allocated ₹${totals.totalAllocated} but Receiving ₹${totals.totalReceived}.`
      );
      return;
    }

    // ─── Build Payload ─────────────────────────────────────────────
    const payload = {
      customerId: customer.id,
      customerType: patientType,
      ipdId: patientType === "IPD" ? customer.ipdId : null,
      receiptDate: null, // server uses NowIST
      payMode: paymentMode,
      cashAmount: parseFloat(cashAmount) || 0,
      upiAmount: parseFloat(upiAmount) || 0,
      totalAmount: totals.totalAllocated,
      remarks: remarks || null,
      idempotencyKey: idempotencyKeyRef.current,
      allocations: allocationEntries.map(([billNo, amt]) => ({
        billNo,
        allocatedAmount: parseFloat(amt),
      })),
    };

    setSubmitting(true);
    try {
      const res = await customFetch('/DuePayment/collect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to collect payment");
      }

      const data = await res.json();
      setSuccess(`✅ Payment collected successfully! Receipt: ${data.receiptNo}`);
      setReceiptData(data);
      setShowReceipt(true);

      // Reset form
      setAllocations({});
      setCashAmount(0);
      setUpiAmount(0);
      setRemarks("");
      idempotencyKeyRef.current = generateIdempotencyKey();

      // Refresh bills list (some may be fully paid now)
      await fetchCustomerDues(customer.id);
    } catch (err) {
      setError(err.message || "Error collecting payment");
    } finally {
      setSubmitting(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="container-fluid mt-0">
      <div className="card shadow">
        <div className="card-header py-2 bg-warning">
          <h6 className="mb-0 fw-bold text-dark">
            💰 Medicine Due Payment Collection
          </h6>
        </div>

        <div className="card-body fs-7">
          {error && <div className="alert alert-danger py-2">{error}</div>}
          {success && <div className="alert alert-success py-2">{success}</div>}

          <form onSubmit={handleSubmit}>
            {/* ═══ CUSTOMER SEARCH SECTION ═══ */}
            <div className="border p-2 mb-2 rounded bg-light">
              <div className="row mb-1">
                <div className="col-md-2">
                  <label className="form-label small mb-0">Patient Type</label>
                  <select
                    className="form-select form-select-sm"
                    value={patientType}
                    onChange={(e) => {
                      setPatientType(e.target.value);
                      setCustomer({
                        id: null, name: "", age: "", gender: "",
                        mobileNo: "", address: "", uhidNo: "", ipdId: null,
                      });
                      setPendingBills([]);
                      setAllocations({});
                      setError("");
                    }}
                  >
                    <option value="MEDICUST">MCDC (Pharmacy)</option>
                    <option value="OPD">OPD</option>
                    <option value="IPD">IPD</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label small mb-0">
                    Search Customer <span className="text-danger">*</span>
                  </label>
                  <AsyncSelect
                    key={patientType}
                    cacheOptions
                    loadOptions={loadCustomerOptions}
                    onChange={handleCustomerSelect}
                    placeholder="Type name / mobile to search..."
                    isClearable
                    styles={customStyles}
                    value={
                      customer.id
                        ? {
                            value: customer.id,
                            label: `${customer.name} | UHID: ${customer.uhidNo}`,
                          }
                        : null
                    }
                  />
                </div>

                <div className="col-md-2">
                  <label className="form-label small mb-0">Mobile</label>
                  <input
                    className="form-control form-control-sm"
                    value={customer.mobileNo}
                    readOnly
                  />
                </div>

                <div className="col-md-1">
                  <label className="form-label small mb-0">Age</label>
                  <input
                    className="form-control form-control-sm"
                    value={customer.age}
                    readOnly
                  />
                </div>

                <div className="col-md-1">
                  <label className="form-label small mb-0">Gender</label>
                  <input
                    className="form-control form-control-sm"
                    value={customer.gender}
                    readOnly
                  />
                </div>

                <div className="col-md-2">
                  <label className="form-label small mb-0">Address</label>
                  <input
                    className="form-control form-control-sm"
                    value={customer.address}
                    readOnly
                  />
                </div>
              </div>
            </div>

            {/* ═══ PENDING BILLS TABLE ═══ */}
            {customer.id && (
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <h6 className="mb-0">
                    📋 Pending Bills
                    {pendingBills.length > 0 && (
                      <span className="badge bg-danger ms-2">
                        {pendingBills.length} bills | ₹{totals.totalDue.toFixed(2)} due
                      </span>
                    )}
                  </h6>

                  {pendingBills.length > 0 && (
                    <div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success me-1"
                        onClick={handlePayAllBills}
                      >
                        Pay All Bills
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleClearAllocations}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>

                {loadingBills ? (
                  <div className="text-center py-3 text-muted">
                    Loading pending bills...
                  </div>
                ) : pendingBills.length === 0 ? (
                  <div className="text-center py-3 text-muted">
                    No pending bills for this customer.
                  </div>
                ) : (
                  <div className="table-responsive" style={{ fontSize: '0.8rem' }}>
                    <table className="table table-sm table-bordered table-hover align-middle text-center mb-0">
                      <thead className="table-dark">
                        <tr>
                          <th>#</th>
                          <th>Bill No</th>
                          <th>Bill Date</th>
                          <th>Days</th>
                          <th>Net Amount</th>
                          <th>Paid Earlier</th>
                          <th className="bg-danger text-white">Due Amount</th>
                          <th className="bg-warning text-dark" style={{ width: '140px' }}>
                            Pay Now ₹
                          </th>
                          <th style={{ width: '80px' }}>Quick</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingBills.map((b, idx) => {
                          const currentAlloc = allocations[b.billNo] || '';
                          const isPaid = parseFloat(currentAlloc) >= b.dueAmount - 0.01;
                          return (
                            <tr key={b.billNo} className={isPaid ? 'table-success' : ''}>
                              <td>{idx + 1}</td>
                              <td className="fw-bold text-primary">{b.billNo}</td>
                              <td>{formatDate(b.billDate)}</td>
                              <td>
                                <span className={`badge ${b.daysSinceBill > 30 ? 'bg-danger' : 'bg-secondary'}`}>
                                  {b.daysSinceBill}d
                                </span>
                              </td>
                              <td>₹{Number(b.netAmount).toFixed(2)}</td>
                              <td>₹{Number(b.paidAmount).toFixed(2)}</td>
                              <td className="bg-danger bg-opacity-10 fw-bold">
                                ₹{Number(b.dueAmount).toFixed(2)}
                              </td>
                              <td className="bg-warning bg-opacity-10">
                                <input
                                  type="number"
                                  className="form-control form-control-sm text-end fw-bold"
                                  value={currentAlloc}
                                  onChange={(e) => handleAllocationChange(b.billNo, e.target.value)}
                                  min="0"
                                  max={b.dueAmount}
                                  step="0.01"
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-success py-0 px-2"
                                  onClick={() => handlePayFullBill(b)}
                                  title="Pay full bill"
                                >
                                  Full
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="table-secondary fw-bold">
                        <tr>
                          <td colSpan={6} className="text-end">Totals →</td>
                          <td className="bg-danger text-white">
                            ₹{totals.totalDue.toFixed(2)}
                          </td>
                          <td className="bg-warning text-dark">
                            ₹{totals.totalAllocated.toFixed(2)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ═══ PAYMENT PANEL (only when allocations exist) ═══ */}
            {totals.totalAllocated > 0 && (
              <div className="border p-2 mb-2 rounded bg-info bg-opacity-10">
                <h6 className="mb-2">💳 Payment Details</h6>

                <div className="row g-2 mb-1">
                  <div className="col-md-2">
                    <label className="form-label small mb-0">
                      Payment Mode <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={paymentMode}
                      onChange={(e) => setPaymentMode(e.target.value)}
                    >
                      <option value="CASH">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="BOTH">Cash + UPI</option>
                      <option value="CARD">Card</option>
                    </select>
                  </div>

                  {(paymentMode === "CASH" || paymentMode === "BOTH") && (
                    <div className="col-md-2">
                      <label className="form-label small mb-0">Cash ₹</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={cashAmount}
                        onChange={(e) => setCashAmount(e.target.value)}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  )}

                  {(paymentMode === "UPI" || paymentMode === "BOTH") && (
                    <div className="col-md-2">
                      <label className="form-label small mb-0">UPI ₹</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={upiAmount}
                        onChange={(e) => setUpiAmount(e.target.value)}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  )}

                  <div className="col-md-2">
                    <label className="form-label small mb-0 fw-bold">
                      Total Allocated
                    </label>
                    <input
                      className="form-control form-control-sm fw-bold bg-warning"
                      value={`₹${totals.totalAllocated.toFixed(2)}`}
                      readOnly
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label small mb-0 fw-bold">
                      Total Receiving
                    </label>
                    <input
                      className={`form-control form-control-sm fw-bold ${
                        totals.mismatch ? 'bg-danger text-white' : 'bg-success text-white'
                      }`}
                      value={`₹${totals.totalReceived.toFixed(2)}`}
                      readOnly
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label small mb-0">Balance Due After</label>
                    <input
                      className="form-control form-control-sm"
                      value={`₹${totals.balanceDue.toFixed(2)}`}
                      readOnly
                    />
                  </div>
                </div>

                <div className="row g-2">
                  <div className="col-md-12">
                    <label className="form-label small mb-0">Remarks (optional)</label>
                    <input
                      className="form-control form-control-sm"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Any notes about this collection..."
                      maxLength="500"
                    />
                  </div>
                </div>

                {totals.mismatch && (
                  <div className="alert alert-warning py-1 mt-2 mb-0 small">
                    ⚠️ Allocated total (₹{totals.totalAllocated.toFixed(2)}) does not match
                    Receiving (₹{totals.totalReceived.toFixed(2)}).
                  </div>
                )}
              </div>
            )}

            {/* ═══ SUBMIT BUTTON ═══ */}
            {totals.totalAllocated > 0 && (
              <div className="text-end">
                <button
                  type="submit"
                  className="btn btn-success px-5"
                  disabled={submitting || totals.mismatch}
                >
                  {submitting ? '💾 Processing...' : '💰 Collect Payment'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* ═══ TALLY-STYLE RECEIPT MODAL ═══ */}
      <MedicineDuePaymentReceiptModal
        show={showReceipt}
        receipt={receiptData}
        company={companyInfo}
        onClose={() => setShowReceipt(false)}
        onPrint={() => window.print()}
      />
    </div>
  );
};

export default MedicineDuePaymentCollect;